import React, { useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { blogPosts as staticPosts } from "../mock";
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag, Linkedin, Twitter, Link2 } from "lucide-react";
import { toast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizePost = (post) => ({
  slug: post.slug,
  title: post.title,
  excerpt: post.excerpt,
  tags: post.tags || [],
  date: post.date || (post.created_at ? new Date(post.created_at).toLocaleDateString() : ""),
  readTime: post.read_time || "5 min read",
  cover: post.cover || "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600&q=80&auto=format&fit=crop",
  content: post.content || [],
});

const getBlockKey = (slug, block, index) => {
  const base = block.type === "ul" ? (block.items || []).join("-") : block.text || block.type;
  return `${slug}-${block.type}-${base.slice(0, 40)}-${index}`;
};

const BlogArticle = () => {
  const { slug } = useParams();
  const staticPost = useMemo(() => staticPosts.find((p) => p.slug === slug) || null, [slug]);
  const [post, setPost] = React.useState(staticPost);
  const [loading, setLoading] = React.useState(!staticPost);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    setPost(staticPost);
    if (staticPost) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/blog/posts/${slug}`)
      .then((r) => {
        if (!cancelled) setPost(normalizePost(r.data));
      })
      .catch(() => {
        if (!cancelled) setPost(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, staticPost]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(shareUrl);
    toast({ title: "Link copied", description: "Article URL copied to clipboard." });
  }, [shareUrl]);

  if (loading) {
    return (
      <div className="pt-40 pb-24 container-x text-center text-slate-500">Loading article…</div>
    );
  }

  if (!post) {
    return (
      <div className="pt-40 pb-24 container-x text-center">
        <h1 className="text-3xl font-bold text-[#0B1B3B]">Article not found</h1>
        <Link to="/insights" className="btn-primary mt-6 inline-flex">← Back to blog</Link>
      </div>
    );
  }

  const related = staticPosts.filter((p) => p.slug !== slug && p.tags.some((t) => post.tags.includes(t))).slice(0, 2);

  return (
    <div>
      <section className="relative pt-[120px] pb-16 bg-[#0B1B3B] text-white overflow-hidden">
        <div className="absolute inset-0 grid-lines-bg opacity-40" />
        <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full orb"
             style={{ background: "radial-gradient(closest-side, rgba(30,86,227,0.28), transparent 70%)" }} />
        <div className="container-x relative">
          <Link to="/insights" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <ArrowLeft size={16} /> Back to blog
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {post.tags.map((t) => (
              <span key={t} className="text-[11px] font-semibold uppercase tracking-wider bg-white/10 border border-white/10 px-3 py-1 rounded-full">{t}</span>
            ))}
          </div>
          <h1 className="mt-4 text-3xl md:text-5xl font-extrabold leading-tight max-w-4xl">{post.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-slate-300">
            <span className="inline-flex items-center gap-2"><Calendar size={14} /> {post.date}</span>
            <span className="h-1 w-1 rounded-full bg-slate-500" />
            <span className="inline-flex items-center gap-2"><Clock size={14} /> {post.readTime}</span>
            <span className="h-1 w-1 rounded-full bg-slate-500" />
            <span>by Vishwajeet Bhangare</span>
          </div>
        </div>
      </section>

      <div className="container-x -mt-8 relative z-10">
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-2xl">
          <img src={post.cover} alt={post.title} className="w-full h-[380px] object-cover" />
        </div>
      </div>

      <section className="py-16 bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3 lg:sticky lg:top-28 self-start space-y-6">
            <div className="bg-[#F5F7FB] border border-slate-200 rounded-2xl p-5">
              <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500">Share</h4>
              <div className="mt-4 flex items-center gap-2">
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full border border-slate-200 hover:border-[#1E56E3] hover:text-[#1E56E3] flex items-center justify-center"><Linkedin size={14} /></a>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full border border-slate-200 hover:border-[#1E56E3] hover:text-[#1E56E3] flex items-center justify-center"><Twitter size={14} /></a>
                <button onClick={copyLink} className="h-9 w-9 rounded-full border border-slate-200 hover:border-[#1E56E3] hover:text-[#1E56E3] flex items-center justify-center"><Link2 size={14} /></button>
              </div>
            </div>
            <div className="bg-[#F5F7FB] border border-slate-200 rounded-2xl p-5">
              <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500">Topics</h4>
              <div className="mt-4 flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="text-[11px] font-medium bg-white text-[#1E56E3] px-2.5 py-1 rounded-full inline-flex items-center gap-1 border border-slate-200">
                    <Tag size={11} /> {t}
                  </span>
                ))}
              </div>
            </div>
          </aside>

          <article className="lg:col-span-9 prose max-w-none space-y-6">
            {(post.content || []).map((block, i) => {
              const blockKey = getBlockKey(slug, block, i);
              if (block.type === "h") return <h2 key={blockKey} className="text-2xl md:text-3xl font-bold text-[#0B1B3B] mt-4">{block.text}</h2>;
              if (block.type === "ul")
                return (
                  <ul key={blockKey} className="space-y-3">
                    {(block.items || []).map((it, j) => (
                      <li key={`${blockKey}-${it.slice(0, 40)}-${j}`} className="flex items-start gap-3 text-[16px] text-slate-700 leading-relaxed">
                        <span className="mt-2 h-2 w-2 rounded-full bg-[#1E56E3] shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                );
              return <p key={blockKey} className="text-[16px] text-slate-700 leading-relaxed">{block.text}</p>;
            })}

            <div className="mt-10 rounded-2xl border border-slate-200 p-6 bg-gradient-to-br from-[#F5F7FB] to-white">
              <h3 className="text-lg font-bold text-[#0B1B3B]">Have a similar challenge?</h3>
              <p className="mt-2 text-sm text-slate-600">Let’s talk about how V4 can bring these outcomes to your data platform.</p>
              <Link to="/contact" className="btn-primary mt-4 inline-flex">Start a conversation <ArrowRight size={16} /></Link>
            </div>
          </article>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-[#F5F7FB]">
          <div className="container-x">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0B1B3B]">Related articles</h2>
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-slate-200 hover-lift block">
                  <div className="relative h-48 overflow-hidden">
                    <img src={r.cover} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3B]/70 to-transparent" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-[#0B1B3B] group-hover:text-[#1E56E3] transition-colors">{r.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 line-clamp-2">{r.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogArticle;
